import { useMemo } from 'react';

import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import type { PettyCashReplenishmentWithEntries } from '@dcc-erp/shared';

import type { Branch } from './pc-helpers';

export interface PCReplenProps {
  branches: readonly Branch[];
  replenishments: readonly PettyCashReplenishmentWithEntries[];
  onReplenish: (floatId: string) => void;
}

export function PCReplen({ branches, replenishments, onReplenish }: PCReplenProps) {
  // Needs-replenish queue: branches with any outstanding used amount, sorted by
  // usedPct descending so most-urgent floats rise to the top.
  const queue = useMemo(
    () =>
      branches
        .filter((b) => b.used > 0)
        .slice()
        .sort((a, b) => b.usedPct - a.usedPct),
    [branches],
  );

  // KPI derivations from real data.
  const needsReplenCount = queue.length;
  const needsReplenTotal = queue.reduce((sum, b) => sum + b.used, 0);
  const nearEmptyCount = branches.filter((b) => b.usedPct >= 90).length;
  const replenHistoryTotal = replenishments.reduce((sum, r) => sum + Number(r.amount), 0);

  return (
    <>
      {/* Section 1: KPI cards driven by real data */}
      <div className="grid-3">
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            รอชดเชย (มียอดใช้ไป)
          </div>
          <div className="value tnum">
            {fmt(needsReplenCount)} <span className="unit">สาขา</span>
          </div>
          <div className="trend faint">รวม {fmtB(needsReplenTotal)} บาท</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            วงเงินใกล้หมด ({`> 90%`})
          </div>
          <div className="value tnum">
            {fmt(nearEmptyCount)} <span className="unit">สาขา</span>
          </div>
          <div className="trend down">ควรเร่งชดเชย</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            ใบเบิกชดเชยทั้งหมด
          </div>
          <div className="value tnum">
            {fmt(replenishments.length)} <span className="unit">ใบ</span>
          </div>
          <div className="trend faint">รวม {fmtB(replenHistoryTotal)} บาท</div>
        </div>
      </div>

      {/* Section 2: Needs-replenish queue */}
      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontWeight: 600, fontSize: 14 }}>คิวรอชดเชย</span>
          <span className="muted" style={{ fontSize: 13 }}>
            เรียงตาม % การใช้วงเงิน (มาก → น้อย)
          </span>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 400 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>สาขา</th>
                <th>ผู้รับผิดชอบ</th>
                <th className="num">วงเงิน</th>
                <th className="num">ใช้ไป</th>
                <th style={{ width: 160 }}>การใช้วงเงิน</th>
                <th style={{ width: 100 }}>การกระทำ</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}
                  >
                    ไม่มีสาขาที่มียอดค้างชดเชย
                  </td>
                </tr>
              ) : (
                queue.map((b) => (
                  <tr key={b.id}>
                    <td>
                      {b.id} — {b.name}
                    </td>
                    <td className="muted">{b.custodian || '—'}</td>
                    <td className="num tnum">{fmt(b.float)}</td>
                    <td className="num tnum">{fmt(b.used)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            background: '#f0f0f3',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${b.usedPct}%`,
                              height: '100%',
                              background:
                                b.usedPct >= 90
                                  ? 'var(--negative)'
                                  : b.usedPct >= 70
                                    ? 'var(--warning)'
                                    : 'var(--positive)',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 14,
                            color: 'var(--text-muted)',
                            minWidth: 32,
                            textAlign: 'right',
                          }}
                        >
                          {b.usedPct}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn sm"
                        style={{ height: 26, fontSize: 14 }}
                        onClick={() => onReplenish(b.floatId)}
                      >
                        ชดเชย →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Replenishment history */}
      <div className="tbl-shell" style={{ marginTop: 16 }}>
        <div className="filter-row">
          <span style={{ fontWeight: 600, fontSize: 14 }}>ประวัติการชดเชย</span>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 400 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>เลขที่ใบเบิก</th>
                <th>วันที่</th>
                <th className="num">จำนวนรายการ</th>
                <th className="num">ยอดชดเชย</th>
                <th>บัญชีต้นทาง</th>
                <th>JV</th>
              </tr>
            </thead>
            <tbody>
              {replenishments.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}
                  >
                    ยังไม่มีประวัติการชดเชยในงวดนี้
                  </td>
                </tr>
              ) : (
                replenishments.map((r) => (
                  <tr key={r.id}>
                    <td className="code" style={{ color: 'var(--brand)', fontWeight: 500 }}>
                      {r.voucherNo}
                    </td>
                    <td>{fmtDate(r.replenishDate)}</td>
                    <td className="num tnum">{fmt(r.entries.length)}</td>
                    <td className="num tnum">
                      <b>{fmt(Number(r.amount))}</b>
                    </td>
                    <td className="code">{r.fromAccountCode}</td>
                    <td>
                      {r.journalEntryId ? (
                        <span className="muted" title={r.journalEntryId} style={{ fontSize: 13 }}>
                          {r.journalEntryId.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
